import { useState, useMemo, useRef, useEffect } from "react";
import {
  BarChart3, Star, Store, BookOpen, QrCode, Users, TrendingUp, TrendingDown,
  Download, Plus, X, Pencil, Menu, Zap, Settings, Share2,
} from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import Logo from "./Logo";
import { money, METHOD_LABELS, post, patch } from "../api";
import { RollingNumber, useMotion, SPRING } from "../motion";
import { motion } from "framer-motion";

const METHOD_COLORS = { mercadopago: "#A0CDE8", tarjeta: "#0A0A0A", efectivo: "#16A34A", transferencia: "#EAB308", modo: "#DC2626" };
const CATS = ["entradas", "principales", "pastas", "postres", "bebidas", "vinos"];

export default function DuenoView({ state, setState, refresh, isMobile, section, setSection }) {
  const [editDish, setEditDish] = useState(null);
  const [newDish, setNewDish] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const hiResRef = useRef(null);

  const sales = state?.sales || [];
  const reviews = state?.reviews || [];
  const dishes = state?.dishes || [];
  const team = state?.team || [];
  const branches = state?.branches || [];
  const topDishes = state?.top_dishes || [];
  const bottomDishes = state?.bottom_dishes || [];
  const impact = state?.impact || {};
  const contacts = state?.contacts || [];
  const config = state?.config || {};
  const mozos = state?.mozos || [];
  const waiterTips = state?.waiter_tips || {};

  const exportContacts = () => {
    const csv = "tipo,contacto,mesa,fecha\n" + contacts.map((c) => `${c.kind},${c.value},${c.table},${c.at}`).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = "contactos-bodegon-demo.csv";
    a.click();
  };

  const Sparkline = ({ data, color = "#A0CDE8" }) => {
    const max = Math.max(...data, 1);
    return (
      <div className="flex items-end gap-1 h-8 mt-3">
        {data.map((v, i) => (
          <div key={i} className="flex-1 rounded-sm" style={{ height: `${Math.max(8, (v / max) * 100)}%`, background: color, opacity: 0.35 + (i / data.length) * 0.65 }} />
        ))}
      </div>
    );
  };

  const impactCards = [
    { key: "tip_pct", label: "propina promedio", unit: "%", before: impact.tip_pct?.before, with: impact.tip_pct?.with, weekly: impact.tip_pct?.weekly_with || [], fmt: (v) => `${v}%`, testid: "impact-tip" },
    { key: "reviews_5", label: "reseñas de 5★ por semana", unit: "", before: impact.reviews_5?.before, with: impact.reviews_5?.with, weekly: impact.reviews_5?.weekly_with || [], fmt: (v) => v, testid: "impact-reviews" },
    { key: "minutes_saved", label: "minutos ahorrados por mesa", unit: " min", before: impact.minutes_saved?.before, with: impact.minutes_saved?.with, weekly: impact.minutes_saved?.weekly_with || [], fmt: (v) => `${v} min`, testid: "impact-minutes" },
    { key: "qr_pay_pct", label: "mesas que pagan por QR", unit: "%", before: impact.qr_pay_pct?.before, with: impact.qr_pay_pct?.with, weekly: impact.qr_pay_pct?.weekly_with || [], fmt: (v) => `${v}%`, testid: "impact-qr" },
  ];

  const Impacto = (
    <div data-testid="dueno-impacto">
      <div className={`grid ${isMobile ? "grid-cols-1" : "grid-cols-2"} gap-4`}>
        {impactCards.map((c) => (
          <div key={c.key} className="bg-white border border-[#E5E7EB] rounded-lg p-5" data-testid={c.testid}>
            <p className="text-xs font-bold text-neutral-500 lowercase">{c.label}</p>
            <div className="flex items-end justify-between mt-3">
              <div>
                <p className="text-[10px] text-neutral-400 lowercase">antes del QR</p>
                <p className="font-mono2 text-sm font-bold text-neutral-400">{c.fmt(c.before ?? 0)}</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] font-bold text-[#16A34A] lowercase">con QR</p>
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 0.3 }}>
                  <RollingNumber value={c.with ?? 0} duration={0.6} format={(v) => c.fmt(Number(v.toFixed(1)))} className="font-mono2 font-extrabold text-3xl" />
                </motion.div>
              </div>
            </div>
            <Sparkline data={c.weekly} />
            <p className="text-[10px] text-neutral-400 mt-2">últimas 4 semanas</p>
          </div>
        ))}
        <div className="bg-[#0A0A0A] text-white rounded-lg p-5" data-testid="impact-contacts">
          <p className="text-xs font-bold text-white/50 lowercase">contactos captados</p>
          <p className="font-mono2 font-extrabold text-3xl mt-2" data-testid="impact-contacts-count">{contacts.length}</p>
          <p className="text-xs text-white/50 mt-1">WhatsApp o email que dejan al pedir el comprobante</p>
          <div className="max-h-24 overflow-y-auto mt-3 space-y-1">
            {contacts.slice(0, 6).map((c) => (
              <p key={c.id} className="text-[11px] font-mono2 text-white/70 truncate">{c.kind === "whatsapp" ? "WA" : "@"} · {c.value}</p>
            ))}
          </div>
          <button data-testid="export-contacts" onClick={exportContacts}
            className="mt-3 w-full flex items-center justify-center gap-2 bg-[#A0CDE8] text-[#0A0A0A] font-bold text-xs py-2.5 rounded-lg hover:brightness-95 transition">
            <Share2 size={13} /> exportar
          </button>
        </div>
      </div>
      <p className="text-[11px] text-neutral-400 mt-4">datos simulados · lo que hacés en la demo suma en vivo</p>
    </div>
  );

  const todayStr = new Date().toISOString().slice(0, 10);
  const dAgo = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

  const windowStats = (from, to, branch = "palermo") => {
    const rows = sales.filter((s) => s.branch === branch && s.date >= from && s.date <= to);
    const total = rows.reduce((a, r) => a + r.total, 0);
    const tables = rows.reduce((a, r) => a + r.tables, 0);
    const kitchen = rows.length ? rows.reduce((a, r) => a + r.avg_kitchen_minutes, 0) / rows.length : 0;
    return { total, tables, avgTicket: tables ? Math.round(total / tables) : 0, kitchen: kitchen.toFixed(1) };
  };

  // comparación mensual completa vs proyección honesta para períodos cortos
  const delta = (a, b) => (b ? Math.max(-99, Math.min(99, ((a - b) / b) * 100)).toFixed(1) : "0.0");
  const deltaLabel = (a, b) => (a >= b ? `+${delta(a, b)}%` : `${delta(a, b)}%`);

  const kpis = [
    { label: "ventas de hoy", cur: windowStats(todayStr, todayStr), prev: windowStats(dAgo(1), dAgo(1)), cmp: "vs ayer", testid: "kpi-day" },
    { label: "esta semana", cur: windowStats(dAgo(6), todayStr), prev: windowStats(dAgo(13), dAgo(7)), cmp: "vs semana anterior", testid: "kpi-week" },
    { label: "este mes", cur: windowStats(dAgo(29), todayStr), prev: windowStats(dAgo(59), dAgo(30)), cmp: "vs mes anterior", testid: "kpi-month" },
  ];

  const hourData = useMemo(() => {
    // promedio de los últimos 30 días (el texto de picos usa estos mismos datos)
    const acc = {};
    const from = new Date(Date.now() - 29 * 86400000).toISOString().slice(0, 10);
    const days = sales.filter((s) => s.branch === "palermo" && s.date >= from);
    days.forEach((s) => {
      (s.rows || []).forEach((r) => { acc[r.hour] = (acc[r.hour] || 0) + r.amount; });
    });
    const n = days.length || 1;
    return Object.keys(acc).sort((a, b) => a - b).map((h) => ({ hora: `${h}:00`, ventas: Math.round(acc[h] / n) }));
  }, [sales]);

  const peakHours = useMemo(() => {
    if (!hourData.length) return "";
    const sorted = [...hourData].sort((a, b) => b.ventas - a.ventas).slice(0, 2);
    return sorted.map((h) => h.hora).sort().join(" y ");
  }, [hourData]);

  const methodData = useMemo(() => {
    const acc = {};
    const from = new Date(Date.now() - 29 * 86400000).toISOString().slice(0, 10);
    sales.filter((s) => s.branch === "palermo" && s.date >= from).forEach((s) => {
      (s.rows || []).forEach((r) => { acc[r.method] = (acc[r.method] || 0) + r.amount; });
    });
    return Object.entries(acc).map(([m, v]) => ({ name: METHOD_LABELS[m], value: v, color: METHOD_COLORS[m] }));
  }, [sales]);

  const rep = useMemo(() => {
    const avg = reviews.length ? (reviews.reduce((a, r) => a + r.stars, 0) / reviews.length).toFixed(1) : "0";
    const google = reviews.filter((r) => r.route === "google").length;
    const privado = reviews.filter((r) => r.route === "privado").length;
    const monthAgo = new Date(Date.now() - 30 * 86400000);
    const privadoMes = reviews.filter((r) => r.route === "privado" && new Date(r.at) >= monthAgo).length;
    return { avg, google, privado, privadoMes, total: reviews.length };
  }, [reviews]);

  const palermo = windowStats(dAgo(29), todayStr, "palermo");
  const sanTelmo = windowStats(dAgo(29), todayStr, "san_telmo");
  const repByBranch = (b) => {
    const rs = reviews.filter((r) => r.branch === b);
    return rs.length ? (rs.reduce((a, r) => a + r.stars, 0) / rs.length).toFixed(1) : "—";
  };

  const saveDish = async (d) => { const res = await patch(`/dishes/${d.id}`, d); setState(res.state); setEditDish(null); };
  const addDish = async () => { const res = await post("/dishes", newDish); setState(res.state); setNewDish(null); };

  const tableUrl = (n) => `${window.location.origin}/?mesa=${n}`;

  // descarga en alta resolución: se genera un QR nativo de 960 px (no se agranda el chico de pantalla)
  const [hiResFor, setHiResFor] = useState(null);
  const downloadQR = (n) => setHiResFor(n);
  useEffect(() => {
    if (hiResFor == null) return;
    const big = hiResRef.current || document.getElementById("qr-hires");
    if (!big) return;
    const n = hiResFor;
    const size = 1024;
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size + 140;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(big, 32, 32, size - 64, size - 64);
    ctx.fillStyle = "#0A0A0A";
    ctx.font = "bold 56px Inter, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(`Bodegón Demo · Mesa ${n}`, size / 2, size + 80);
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = `bodegon-demo-mesa-${n}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setHiResFor(null);
  }, [hiResFor]);

  const Configuracion = (
    <div className="space-y-4" data-testid="dueno-config">
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-5">
        <p className="font-extrabold lowercase mb-1">modo de servicio</p>
        <p className="text-xs text-neutral-500 mb-3">Al cambiarlo, el flujo del cliente cambia en vivo.</p>
        <div className="grid grid-cols-3 gap-2">
          {[
            { id: "solo_pedir", label: "solo pedir", desc: "el mozo cobra" },
            { id: "pagar_al_final", label: "pedir y pagar al final", desc: "el actual" },
            { id: "pagar_al_instante", label: "pagar al instante", desc: "autoservicio" },
          ].map((m) => (
            <button key={m.id} data-testid={`mode-${m.id}`} onClick={() => patch("/config", { service_mode: m.id }).then((r) => setState(r.state))}
              className={`rounded-lg border-2 p-3 text-left transition ${config.service_mode === m.id ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E7EB] hover:border-[#0A0A0A]"}`}>
              <p className="text-xs font-bold">{m.label}</p>
              <p className={`text-[10px] mt-0.5 ${config.service_mode === m.id ? "text-white/60" : "text-neutral-400"}`}>{m.desc}</p>
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-lg p-5">
        <p className="font-extrabold lowercase mb-1">qué se puede pedir por QR</p>
        <p className="text-xs text-neutral-500 mb-3">Lo apagado sigue en el menú con la leyenda “pedíselo al mozo”.</p>
        <div className="space-y-2">
          {CATS.map((c) => (
            <div key={c} className="flex items-center justify-between border-b border-[#E5E7EB] pb-2">
              <span className="text-sm font-semibold">{c}</span>
              <button data-testid={`cfg-cat-${c}`} onClick={() => patch("/config", { categories_enabled: { ...(config.categories_enabled || {}), [c]: !(config.categories_enabled || {})[c] } }).then((r) => setState(r.state))}
                className={`switch-toggle relative w-11 h-6 rounded-full transition-colors ${(config.categories_enabled || {})[c] !== false ? "bg-[#16A34A]" : "bg-neutral-300"}`}>
                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${(config.categories_enabled || {})[c] !== false ? "left-5" : "left-0.5"}`} />
              </button>
            </div>
          ))}
        </div>
        <div className="space-y-1.5 mt-3">
          {dishes.map((d) => (
            <div key={d.id} className="flex items-center justify-between text-xs py-1">
              <span className="truncate flex-1">{d.name}</span>
              <button data-testid={`cfg-dish-${d.id}`} onClick={() => patch("/config", { dishes_enabled: { ...(config.dishes_enabled || {}), [d.id]: !(config.dishes_enabled || {})[d.id] } }).then((r) => setState(r.state))}
                className={`switch-toggle relative w-9 h-5 rounded-full transition-colors shrink-0 ${(config.dishes_enabled || {})[d.id] !== false ? "bg-[#16A34A]" : "bg-neutral-300"}`}>
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${(config.dishes_enabled || {})[d.id] !== false ? "left-4" : "left-0.5"}`} />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-lg p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-extrabold lowercase">aprobar el primer pedido</p>
            <p className="text-xs text-neutral-500 mt-0.5">El primer pedido de cada mesa espera tu OK; los siguientes pasan directo.</p>
          </div>
          <button data-testid="cfg-approve-first" onClick={() => patch("/config", { approve_first_order: !config.approve_first_order }).then((r) => setState(r.state))}
            className={`switch-toggle relative w-12 h-7 rounded-full transition-colors shrink-0 ${config.approve_first_order ? "bg-[#16A34A]" : "bg-neutral-300"}`}>
            <span className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-all ${config.approve_first_order ? "left-6" : "left-1"}`} />
          </button>
        </div>
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-lg p-5">
        <p className="font-extrabold lowercase mb-1">a dónde va la propina</p>
        <p className="text-xs text-neutral-500 mb-3">El comprobante del cliente muestra a quién fue.</p>
        <div className="grid grid-cols-3 gap-2">
          {[
            { id: "mozo", label: "al mozo de la mesa" },
            { id: "local", label: "al local" },
            { id: "pozo", label: "a un pozo común" },
          ].map((m) => (
            <button key={m.id} data-testid={`tipdest-${m.id}`} onClick={() => patch("/config", { tip_destination: m.id }).then((r) => setState(r.state))}
              className={`rounded-lg border-2 p-3 text-xs font-bold transition ${config.tip_destination === m.id ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E7EB] hover:border-[#0A0A0A]"}`}>
              {m.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const nav = [
    { id: "impacto", label: "impacto", icon: <Zap size={17} /> },
    { id: "resumen", label: "resumen", icon: <BarChart3 size={17} /> },
    { id: "reputacion", label: "reputación", icon: <Star size={17} /> },
    { id: "sucursales", label: "sucursales", icon: <Store size={17} /> },
    { id: "menu", label: "menú", icon: <BookOpen size={17} /> },
    { id: "qr", label: "QR por mesa", icon: <QrCode size={17} /> },
    { id: "equipo", label: "equipo", icon: <Users size={17} /> },
    { id: "configuracion", label: "configuración", icon: <Settings size={17} /> },
  ];

  const Delta = ({ a, b, cmp }) => {
    const up = a >= b;
    return (
      <span className={`flex items-center gap-1 text-[11px] font-bold font-mono2 ${up ? "text-[#16A34A]" : "text-[#DC2626]"}`}>
        {up ? <TrendingUp size={12} /> : <TrendingDown size={12} />} {deltaLabel(a, b)} <span className="font-normal text-neutral-400">{cmp}</span>
      </span>
    );
  };

  // ---------- secciones ----------

  const Resumen = (
    <div className="space-y-6" data-testid="dueno-resumen">
      <div className={`grid ${isMobile ? "grid-cols-1" : "grid-cols-3"} gap-4`}>
        {kpis.map((k) => (
          <div key={k.label} className="bg-white border border-[#E5E7EB] rounded-lg p-5">
            <div className="flex items-center justify-between">
              <p className="text-xs font-bold text-neutral-500 lowercase">{k.label}</p>
              <Delta a={k.cur.total} b={k.prev.total} cmp={k.cmp} />
            </div>
            <RollingNumber value={k.cur.total} duration={0.8} format={(v) => money(v)} className="font-mono2 font-extrabold text-2xl mt-2 block" />
            <div className="flex gap-4 mt-3 text-[11px] text-neutral-500 flex-wrap">
              <span>mesas <b className="font-mono2 text-[#0A0A0A]">{k.cur.tables}</b></span>
              <span>ticket prom. <b className="font-mono2 text-[#0A0A0A]">{money(k.cur.avgTicket)}</b></span>
              <span>cocina <b className="font-mono2 text-[#0A0A0A]">{k.cur.kitchen} min</b></span>
            </div>
          </div>
        ))}
      </div>

      <div className={`grid ${isMobile ? "grid-cols-1" : "grid-cols-2"} gap-4`}>
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-5">
          <p className="font-extrabold lowercase mb-1">ventas por hora <span className="text-xs font-normal text-neutral-400">(promedio mensual)</span></p>
          <p className="text-xs text-neutral-500 mb-4">picos a las {peakHours}: ahí necesitás más gente en cocina.</p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={hourData} margin={{ left: -18 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
              <XAxis dataKey="hora" tick={{ fontSize: 12, fontFamily: "JetBrains Mono", fill: "#525252" }} stroke="#E5E7EB" />
              <YAxis tick={{ fontSize: 12, fontFamily: "JetBrains Mono", fill: "#525252" }} stroke="#E5E7EB" tickFormatter={(v) => `$${Math.round(v / 1000)}k`} />
              <Tooltip formatter={(v) => [money(v), "ventas"]} contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB", fontFamily: "JetBrains Mono", fontSize: 12 }} />
              <Bar dataKey="ventas" fill="#A0CDE8" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-5">
          <p className="font-extrabold lowercase mb-4">ventas por medio de pago</p>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={methodData} dataKey="value" nameKey="name" innerRadius={52} outerRadius={80} paddingAngle={2} strokeWidth={0}>
                {methodData.map((m, i) => <Cell key={i} fill={m.color} />)}
              </Pie>
              <Tooltip formatter={(v) => money(v)} contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB", fontFamily: "JetBrains Mono", fontSize: 12 }} />
              <Legend iconSize={10} wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className={`grid ${isMobile ? "grid-cols-1" : "grid-cols-2"} gap-4`}>
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-5">
          <p className="font-extrabold lowercase mb-4">los más vendidos</p>
          <div className="space-y-2.5">
            {topDishes.map(([name, qty], i) => (
              <div key={name} className="flex items-center gap-3">
                <span className="font-mono2 text-xs font-bold text-neutral-400 w-4">{i + 1}</span>
                <span className="text-sm font-semibold flex-1 truncate">{name}</span>
                <motion.div className="h-2 rounded-full bg-[#A0CDE8]"
                  initial={{ width: 0 }} animate={{ width: `${Math.max(8, (qty / topDishes[0][1]) * 120)}px` }}
                  transition={{ ...SPRING, delay: i * 0.06 }} />
                <span className="font-mono2 text-xs font-bold">{qty}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-5">
          <p className="font-extrabold lowercase mb-4">los menos vendidos <span className="text-xs font-normal text-neutral-400">· candidatos a salir de la carta</span></p>
          <div className="space-y-2.5">
            {bottomDishes.map(([name, qty]) => (
              <div key={name} className="flex items-center justify-between">
                <span className="text-sm font-semibold flex-1 truncate">{name}</span>
                <span className="font-mono2 text-xs font-bold text-[#DC2626]">{qty}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const Reputacion = (
    <div className="space-y-6" data-testid="dueno-reputacion">
      <div className={`grid ${isMobile ? "grid-cols-1" : "grid-cols-4"} gap-4`}>
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-5">
          <p className="text-xs font-bold text-neutral-500 lowercase">promedio de estrellas</p>
          <p className="font-mono2 font-extrabold text-3xl mt-2" data-testid="rep-avg">{rep.avg}★</p>
          <p className="text-xs text-neutral-400 mt-1">{rep.total} reseñas</p>
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-5">
          <p className="text-xs font-bold text-neutral-500 lowercase">derivados a Google</p>
          <p className="font-mono2 font-extrabold text-3xl mt-2 text-[#16A34A]" data-testid="rep-google">{rep.google}</p>
          <p className="text-xs text-neutral-400 mt-1">reseñas públicas de 4-5★</p>
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-5">
          <p className="text-xs font-bold text-neutral-500 lowercase">feedback que quedó adentro</p>
          <p className="font-mono2 font-extrabold text-3xl mt-2 text-[#DC2626]" data-testid="rep-privado">{rep.privado}</p>
          <p className="text-xs text-neutral-400 mt-1">1-3★ llegó a gerencia, no a Google</p>
        </div>
        <div className="bg-[#A0CDE8] text-[#0A0A0A] rounded-lg p-5">
          <p className="text-xs font-bold lowercase opacity-70">lo que te ahorraste</p>
          <p className="font-extrabold text-2xl mt-2 font-mono2" data-testid="rep-saved">{rep.privadoMes}</p>
          <p className="text-xs mt-1">reseñas negativas que no llegaron a Google este mes</p>
        </div>
      </div>
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-5">
        <p className="font-extrabold lowercase mb-4">últimas reseñas</p>
        <div className="space-y-2">
          {reviews.slice(0, 10).map((r) => (
            <div key={r.id} className="flex items-center gap-3 text-sm border-b border-[#E5E7EB] last:border-0 py-2" data-testid={`review-row-${r.id}`}>
              <span className={`font-mono2 font-bold w-10 ${r.stars >= 4 ? "text-[#16A34A]" : "text-[#DC2626]"}`}>{r.stars}★</span>
              <span className="flex-1 truncate">{r.text}</span>
              <span className="text-xs text-neutral-400 hidden sm:inline">Mesa {r.table}</span>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${r.route === "google" ? "bg-[#DCFCE7] text-[#16A34A]" : r.route === "privado" ? "bg-[#FEE2E2] text-[#DC2626]" : "bg-[#F2F2ED] text-neutral-500"}`}>
                {r.route}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const BranchCard = ({ b, stats, stars, testid }) => (
    <div className="bg-white border border-[#E5E7EB] rounded-lg p-6" data-testid={testid}>
      <p className="font-extrabold text-lg">{b.name}</p>
      <p className="text-xs text-neutral-500 mb-5">{b.address}</p>
      <div className="space-y-3 text-sm">
        <div className="flex justify-between"><span className="text-neutral-500">ventas del mes</span><span className="font-mono2 font-bold">{money(stats.total)}</span></div>
        <div className="flex justify-between"><span className="text-neutral-500">mesas atendidas</span><span className="font-mono2 font-bold">{stats.tables}</span></div>
        <div className="flex justify-between"><span className="text-neutral-500">ticket promedio</span><span className="font-mono2 font-bold">{money(stats.avgTicket)}</span></div>
        <div className="flex justify-between"><span className="text-neutral-500">tiempo de cocina</span><span className="font-mono2 font-bold">{stats.kitchen} min</span></div>
        <div className="flex justify-between"><span className="text-neutral-500">reseñas</span><span className="font-mono2 font-bold">{stars}★</span></div>
      </div>
    </div>
  );

  const conclusion = useMemo(() => {
    if (palermo.total > sanTelmo.total && sanTelmo.avgTicket > palermo.avgTicket) {
      return `Palermo vende más en volumen (${money(palermo.total)} vs ${money(sanTelmo.total)}), pero San Telmo tiene mejor ticket promedio (${money(sanTelmo.avgTicket)} vs ${money(palermo.avgTicket)}) y mejores reseñas: el local chico rinde mejor por mesa.`;
    }
    return "Compará volumen, ticket y reseñas para decidir dónde invertir el próximo turno extra.";
  }, [palermo, sanTelmo]);

  const Sucursales = (
    <div data-testid="dueno-sucursales">
      <div className={`grid ${isMobile ? "grid-cols-1" : "grid-cols-2"} gap-4`}>
        <BranchCard b={branches[0]} stats={palermo} stars={repByBranch("palermo")} testid="branch-palermo" />
        <BranchCard b={branches[1]} stats={sanTelmo} stars={repByBranch("san_telmo")} testid="branch-santelmo" />
      </div>
      <div className="bg-[#E3F0F9] border border-[#A0CDE8] rounded-lg p-5 mt-4" data-testid="branch-conclusion">
        <p className="text-sm font-bold leading-relaxed">{conclusion}</p>
      </div>
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-6 mt-4">
        <p className="font-extrabold lowercase mb-4">comparativa del mes</p>
        {[
          { label: "ventas", a: palermo.total, b: sanTelmo.total, fmt: money },
          { label: "ticket promedio", a: palermo.avgTicket, b: sanTelmo.avgTicket, fmt: money },
          { label: "tiempo cocina (min)", a: parseFloat(palermo.kitchen), b: parseFloat(sanTelmo.kitchen), fmt: (v) => `${v}` },
        ].map((row) => {
          const max = Math.max(row.a, row.b);
          return (
            <div key={row.label} className="mb-4">
              <div className="flex justify-between text-xs font-bold mb-1.5">
                <span className="font-mono2">{row.fmt(row.a)}</span>
                <span className="text-neutral-500 lowercase">{row.label}</span>
                <span className="font-mono2">{row.fmt(row.b)}</span>
              </div>
              <div className="flex gap-2 items-center">
                <div className="flex-1 flex justify-end"><div className="h-3 rounded-l-md bg-[#A0CDE8]" style={{ width: `${(row.a / max) * 100}%` }} /></div>
                <div className="w-px h-5 bg-[#0A0A0A]" />
                <div className="flex-1"><div className="h-3 rounded-r-md bg-[#0A0A0A]" style={{ width: `${(row.b / max) * 100}%` }} /></div>
              </div>
            </div>
          );
        })}
        <div className="flex justify-center gap-6 text-xs font-semibold mt-2">
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[#A0CDE8]" /> Palermo</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[#0A0A0A]" /> San Telmo</span>
        </div>
      </div>
    </div>
  );

  const DishForm = ({ dish, onSave, onCancel, isNew }) => {
    const [f, setF] = useState(dish);
    return (
      <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" data-testid="dish-editor">
        <div className="bg-white rounded-lg p-6 w-[480px] max-w-full max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <p className="font-extrabold lowercase">{isNew ? "plato nuevo" : "editar plato"}</p>
            <button data-testid="dish-editor-close" onClick={onCancel}><X size={18} /></button>
          </div>
          <div className="space-y-3">
            <input data-testid="dish-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="nombre del plato"
              className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#0A0A0A]" />
            <div className="grid grid-cols-2 gap-3">
              <select data-testid="dish-category" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}
                className="border border-[#E5E7EB] rounded-lg px-3 py-2.5 text-sm bg-white outline-none">
                {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <input data-testid="dish-price" type="number" value={f.price} onChange={(e) => setF({ ...f, price: parseInt(e.target.value || "0", 10) })} placeholder="precio"
                className="border border-[#E5E7EB] rounded-lg px-3 py-2.5 text-sm font-mono2 outline-none focus:border-[#0A0A0A]" />
            </div>
            <textarea data-testid="dish-description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={2} placeholder="descripción"
              className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#0A0A0A]" />
            <input data-testid="dish-photo" value={f.photo} onChange={(e) => setF({ ...f, photo: e.target.value })} placeholder="URL de la foto"
              className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2.5 text-xs font-mono2 outline-none focus:border-[#0A0A0A]" />
            {f.photo && <img src={f.photo} alt={f.name || "plato"} className="w-full h-32 object-cover rounded-lg" loading="lazy" />}
            <div className="flex gap-2">
              {["vegano", "sin TACC", "picante"].map((tg) => (
                <button key={tg} data-testid={`dish-tag-${tg}`} onClick={() => setF({ ...f, tags: f.tags.includes(tg) ? f.tags.filter((x) => x !== tg) : [...f.tags, tg] })}
                  className={`text-xs font-bold px-3 py-1.5 rounded-lg border-2 transition ${f.tags.includes(tg) ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E7EB]"}`}>
                  {tg}
                </button>
              ))}
            </div>
            <button data-testid="dish-save" onClick={() => onSave(f)}
              className="w-full bg-[#A0CDE8] text-[#0A0A0A] font-bold py-3 rounded-lg hover:brightness-95 transition">
              guardar
            </button>
          </div>
        </div>
      </div>
    );
  };

  const MenuEditor = (
    <div data-testid="dueno-menu">
      <button data-testid="add-dish-btn" onClick={() => setNewDish({ name: "", category: "principales", price: 15000, description: "", photo: "", tags: [], available: true, kitchen_minutes: 15 })}
        className="mb-4 flex items-center gap-2 bg-[#0A0A0A] text-white text-sm font-bold px-4 py-2.5 rounded-lg hover:bg-neutral-800 transition">
        <Plus size={15} /> plato nuevo
      </button>
      <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="border-b border-[#E5E7EB] text-left text-xs text-neutral-500">
              <th className="px-4 py-3 font-bold lowercase">plato</th>
              <th className="px-4 py-3 font-bold lowercase">categoría</th>
              <th className="px-4 py-3 font-bold lowercase">etiquetas</th>
              <th className="px-4 py-3 font-bold lowercase text-right">precio</th>
              <th className="px-4 py-3 font-bold lowercase text-right">estado</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {dishes.map((d) => (
              <tr key={d.id} className="border-b border-[#E5E7EB] last:border-0 hover:bg-[#FAFAF7]" data-testid={`menu-row-${d.id}`}>
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-3">
                    <img src={d.photo} alt={d.name} className="w-9 h-9 rounded-lg object-cover" loading="lazy" />
                    <span className="font-semibold">{d.name}</span>
                  </div>
                </td>
                <td className="px-4 py-2.5 text-neutral-500">{d.category}</td>
                <td className="px-4 py-2.5">
                  <div className="flex gap-1">
                    {d.tags.map((tg) => <span key={tg} className="text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-[#E3F0F9]">{tg}</span>)}
                  </div>
                </td>
                <td className="px-4 py-2.5 text-right font-mono2 font-bold">{money(d.price)}</td>
                <td className="px-4 py-2.5 text-right">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${d.available ? "bg-[#DCFCE7] text-[#16A34A]" : "bg-neutral-200 text-neutral-600"}`}>
                    {d.available ? "activo" : "agotado"}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-right">
                  <button data-testid={`edit-dish-${d.id}`} onClick={() => setEditDish({ ...d })}
                    className="text-neutral-400 hover:text-[#0A0A0A] transition"><Pencil size={15} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editDish && <DishForm dish={editDish} onSave={saveDish} onCancel={() => setEditDish(null)} />}
      {newDish && <DishForm dish={newDish} isNew onSave={() => addDish()} onCancel={() => setNewDish(null)} />}
    </div>
  );

  const QR = (
    <div data-testid="dueno-qr">
      <p className="text-sm text-neutral-500 mb-5">Cada QR abre la vista cliente de esa mesa en esta demo ({window.location.host}?mesa=N). Lo imprimís a 1024 px, lo pegás en la mesa y listo.</p>
      {hiResFor != null && (
        <div style={{ position: "fixed", left: -10000, top: 0 }} aria-hidden="true">
          <QRCodeCanvas id="qr-hires" ref={hiResRef} value={tableUrl(hiResFor)} size={960} bgColor="#FFFFFF" fgColor="#0A0A0A" level="M" style={{ width: 960, height: 960 }} />
        </div>
      )}
      <div className={`grid ${isMobile ? "grid-cols-2" : "grid-cols-4"} gap-4`}>
        {Array.from({ length: 12 }, (_, i) => i + 1).map((n) => (
          <div key={n} className="bg-white border border-[#E5E7EB] rounded-lg p-4 text-center" data-testid={`qr-card-${n}`}>
            <div className="mx-auto w-fit p-2 border-2 border-[#0A0A0A] rounded-lg bg-white">
              <QRCodeCanvas id={`qr-canvas-${n}`} value={tableUrl(n)} size={96} bgColor="#FFFFFF" fgColor="#0A0A0A" level="M" />
            </div>
            <p className="font-extrabold text-sm mt-2.5">Mesa {n}</p>
            <p className="text-[10px] font-mono2 text-neutral-400 break-all mt-0.5">?mesa={n}</p>
            <button data-testid={`download-qr-${n}`} onClick={() => downloadQR(n)}
              className="mt-2 flex items-center gap-1.5 mx-auto text-xs font-bold bg-[#A0CDE8] text-[#0A0A0A] px-3 py-1.5 rounded-lg hover:brightness-95 transition">
              <Download size={12} /> descargar 1024px
            </button>
          </div>
        ))}
      </div>
    </div>
  );

  const Equipo = (
    <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-x-auto" data-testid="dueno-equipo">
      <table className="w-full text-sm min-w-[560px]">
        <thead>
          <tr className="border-b border-[#E5E7EB] text-left text-xs text-neutral-500">
            <th className="px-5 py-3 font-bold lowercase">nombre</th>
            <th className="px-5 py-3 font-bold lowercase">rol</th>
            <th className="px-5 py-3 font-bold lowercase">permisos</th>
          </tr>
        </thead>
        <tbody>
          {team.map((m) => (
            <tr key={m.id} className="border-b border-[#E5E7EB] last:border-0" data-testid={`team-${m.id}`}>
              <td className="px-5 py-3">
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-[#A0CDE8] flex items-center justify-center font-extrabold text-sm">{m.name[0]}</span>
                  <span className="font-semibold">{m.name}</span>
                </div>
              </td>
              <td className="px-5 py-3">
                <span className="text-xs font-bold bg-[#0A0A0A] text-white px-2 py-1 rounded-md">{m.role}</span>
              </td>
              <td className="px-5 py-3">
                <div className="flex gap-1.5 flex-wrap">
                  {m.permissions.map((p) => (
                    <span key={p} className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-[#F2F2ED] border border-[#E5E7EB]">{p}</span>
                  ))}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const sections = { impacto: Impacto, resumen: Resumen, reputacion: Reputacion, sucursales: Sucursales, menu: MenuEditor, qr: QR, equipo: Equipo, configuracion: Configuracion };

  const NavContent = (
    <nav className="flex-1 p-3 space-y-1">
      {nav.map((n) => (
        <button key={n.id} data-testid={`dueno-nav-${n.id}`} onClick={() => { setSection(n.id); setDrawerOpen(false); }}
          className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm font-semibold transition ${section === n.id ? "bg-[#A0CDE8] text-[#0A0A0A]" : "text-white/70 hover:bg-white/10 hover:text-white"}`}>
          {n.icon} {n.label}
        </button>
      ))}
    </nav>
  );

  return (
    <div className="h-full flex" data-testid="dueno-view">
      {!isMobile && (
        <aside className="w-60 shrink-0 bg-[#0A0A0A] text-white flex flex-col">
          <div className="p-5 flex items-center gap-2.5 border-b border-white/10">
            <Logo size={34} />
            <div>
              <p className="font-extrabold lowercase leading-none">bodegón demo</p>
              <p className="text-[10px] text-white/50 uppercase tracking-widest mt-0.5">dueño</p>
            </div>
          </div>
          {NavContent}
        </aside>
      )}

      {isMobile && drawerOpen && (
        <div className="fixed inset-0 z-50 flex" data-testid="dueno-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDrawerOpen(false)} />
          <aside className="relative w-64 bg-[#0A0A0A] text-white flex flex-col h-full">
            <div className="p-5 flex items-center justify-between border-b border-white/10">
              <div className="flex items-center gap-2.5">
                <Logo size={30} />
                <p className="font-extrabold lowercase">dueño</p>
              </div>
              <button data-testid="dueno-drawer-close" onClick={() => setDrawerOpen(false)}><X size={18} /></button>
            </div>
            {NavContent}
          </aside>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        {isMobile && (
          <div className="shrink-0 bg-white border-b border-[#E5E7EB] px-4 py-2.5 flex items-center gap-3">
            <button data-testid="dueno-menu-btn" onClick={() => setDrawerOpen(true)}
              className="w-8 h-8 rounded-lg border border-[#E5E7EB] flex items-center justify-center">
              <Menu size={16} />
            </button>
            <p className="text-xs text-neutral-400 font-semibold">vista dueño</p>
          </div>
        )}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="flex items-center justify-between mb-5">
            <h1 className="text-2xl font-extrabold lowercase" data-testid="dueno-section-title">{nav.find((n) => n.id === section)?.label}</h1>
            {!isMobile && <span className="font-mono2 text-xs text-neutral-400">vista dueño · datos simulados de 30 días</span>}
          </div>
          {sections[section]}
        </div>
      </div>
    </div>
  );
}
